import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  getDocs, 
  getDoc,
  doc,
  where,
  Timestamp,
  getCountFromServer
} from 'firebase/firestore';
import { db } from '../config/firebase';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [growthData, setGrowthData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [timeframe, setTimeframe] = useState('week');

  const adminEmail = 'hadroncollides@gmail.com';
  
  // Check if current user is admin
  const isAdmin = currentUser && (currentUser.email === adminEmail || currentUser.isAdmin === true);

  useEffect(() => {
    if (isAdmin) {
      loadAdminData();
    }
  }, [isAdmin, timeframe]);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      const [metricsData, usersData, activityData, growthData] = await Promise.all([
        fetchMetrics(),
        fetchAllUsers(),
        fetchRecentActivity(),
        fetchGrowthData()
      ]);
      
      setMetrics(metricsData);
      setAllUsers(usersData);
      setRecentActivity(activityData);
      setGrowthData(growthData);
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
    setLoading(false);
  };

  const fetchMetrics = async () => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const startOfMonth = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Get total users
    const totalUsersSnapshot = await getCountFromServer(collection(db, 'users'));
    const totalUsers = totalUsersSnapshot.data().count;

    // Get active users
    const activeUsersTodayQuery = query(
      collection(db, 'users'),
      where('lastActivityDate', '>=', Timestamp.fromDate(startOfToday))
    );
    const activeUsersWeekQuery = query(
      collection(db, 'users'),
      where('lastActivityDate', '>=', Timestamp.fromDate(startOfWeek))
    );
    const activeUsersMonthQuery = query(
      collection(db, 'users'),
      where('lastActivityDate', '>=', Timestamp.fromDate(startOfMonth))
    );

    const [activeUsersToday, activeUsersWeek, activeUsersMonth] = await Promise.all([
      getCountFromServer(activeUsersTodayQuery),
      getCountFromServer(activeUsersWeekQuery),
      getCountFromServer(activeUsersMonthQuery)
    ]);

    // Get new users
    const newUsersTodayQuery = query(
      collection(db, 'users'),
      where('createdAt', '>=', Timestamp.fromDate(startOfToday))
    );
    const newUsersWeekQuery = query(
      collection(db, 'users'),
      where('createdAt', '>=', Timestamp.fromDate(startOfWeek))
    );
    const newUsersMonthQuery = query(
      collection(db, 'users'),
      where('createdAt', '>=', Timestamp.fromDate(startOfMonth))
    );

    const [newUsersToday, newUsersWeek, newUsersMonth] = await Promise.all([
      getCountFromServer(newUsersTodayQuery),
      getCountFromServer(newUsersWeekQuery),
      getCountFromServer(newUsersMonthQuery)
    ]);

    // Get book clubs count
    const bookClubsSnapshot = await getCountFromServer(collection(db, 'bookClubs'));

    // Calculate total books and lists by iterating through users
    let totalBooks = 0;
    let totalLists = 0;
    let booksAddedToday = 0;
    let booksAddedWeek = 0;
    let booksAddedMonth = 0;

    const usersSnapshot = await getDocs(collection(db, 'users'));
    
    for (const userDoc of usersSnapshot.docs) {
      // Count books for each user
      const userBooksSnapshot = await getCountFromServer(
        collection(db, 'users', userDoc.id, 'books')
      );
      totalBooks += userBooksSnapshot.data().count;

      // Count lists for each user
      const userListsSnapshot = await getCountFromServer(
        collection(db, 'users', userDoc.id, 'bookLists')
      );
      totalLists += userListsSnapshot.data().count;

      // Count books added today
      const booksTodayQuery = query(
        collection(db, 'users', userDoc.id, 'books'),
        where('createdAt', '>=', Timestamp.fromDate(startOfToday))
      );
      const booksTodaySnapshot = await getCountFromServer(booksTodayQuery);
      booksAddedToday += booksTodaySnapshot.data().count;

      // Count books added this week
      const booksWeekQuery = query(
        collection(db, 'users', userDoc.id, 'books'),
        where('createdAt', '>=', Timestamp.fromDate(startOfWeek))
      );
      const booksWeekSnapshot = await getCountFromServer(booksWeekQuery);
      booksAddedWeek += booksWeekSnapshot.data().count;

      // Count books added this month
      const booksMonthQuery = query(
        collection(db, 'users', userDoc.id, 'books'),
        where('createdAt', '>=', Timestamp.fromDate(startOfMonth))
      );
      const booksMonthSnapshot = await getCountFromServer(booksMonthQuery);
      booksAddedMonth += booksMonthSnapshot.data().count;
    }

    return {
      totalUsers,
      activeUsersToday: activeUsersToday.data().count,
      activeUsersWeek: activeUsersWeek.data().count,
      activeUsersMonth: activeUsersMonth.data().count,
      totalBooks,
      totalBookClubs: bookClubsSnapshot.data().count,
      totalBookLists: totalLists,
      newUsersToday: newUsersToday.data().count,
      newUsersWeek: newUsersWeek.data().count,
      newUsersMonth: newUsersMonth.data().count,
      booksAddedToday,
      booksAddedWeek,
      booksAddedMonth,
      timestamp: new Date()
    };
  };

  const fetchAllUsers = async () => {
    const usersQuery = query(
      collection(db, 'users'),
      orderBy('createdAt', 'desc'),
      limit(100)
    );
    
    const snapshot = await getDocs(usersQuery);
    const users = [];
    
    for (const doc of snapshot.docs) {
      const userData = { id: doc.id, ...doc.data() };
      
      // Get additional user stats
      try {
        const [booksSnapshot, listsSnapshot] = await Promise.all([
          getCountFromServer(collection(db, 'users', doc.id, 'books')),
          getCountFromServer(collection(db, 'users', doc.id, 'bookLists'))
        ]);
        
        userData.booksCount = booksSnapshot.data().count;
        userData.listsCount = listsSnapshot.data().count;
      } catch (error) {
        userData.booksCount = 0;
        userData.listsCount = 0;
      }
      
      users.push(userData);
    }
    
    return users;
  };

  const fetchRecentActivity = async () => {
    try {
      const activityQuery = query(
        collection(db, 'admin', 'activity', 'events'),
        orderBy('timestamp', 'desc'),
        limit(100)
      );
      
      const snapshot = await getDocs(activityQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.log('No activity data found:', error);
      return [];
    }
  };

  const fetchGrowthData = async () => {
    const days = timeframe === 'day' ? 1 : timeframe === 'week' ? 7 : 30;
    const data = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const startOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const endOfDay = new Date(startOfDay.getTime() + 24 * 60 * 60 * 1000);
      
      try {
        // Get new users for this day
        const newUsersQuery = query(
          collection(db, 'users'),
          where('createdAt', '>=', Timestamp.fromDate(startOfDay)),
          where('createdAt', '<', Timestamp.fromDate(endOfDay))
        );
        const newUsersSnapshot = await getCountFromServer(newUsersQuery);
        
        data.push({
          date: startOfDay,
          users: newUsersSnapshot.data().count,
          books: 0 // Simplified for performance
        });
      } catch (error) {
        data.push({
          date: startOfDay,
          users: 0,
          books: 0
        });
      }
    }
    
    return data;
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  if (!isAdmin) {
    return (
      <div className="admin-dashboard">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <div className="last-updated">
          Last updated: {metrics?.timestamp ? new Date(metrics.timestamp).toLocaleString() : 'Unknown'}
        </div>
      </div>

      <div className="dashboard-tabs">
        {['overview', 'users', 'activity', 'growth'].map(tab => (
          <button
            key={tab}
            className={`tab-button ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <div className="dashboard-content">
        {activeTab === 'overview' && (
          <div className="overview-section">
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-icon">👥</div>
                <div className="metric-info">
                  <h3>{metrics?.totalUsers || 0}</h3>
                  <p>Total Users</p>
                  <span className="metric-change">+{metrics?.newUsersToday || 0} today</span>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">🟢</div>
                <div className="metric-info">
                  <h3>{metrics?.activeUsersToday || 0}</h3>
                  <p>Active Today</p>
                  <span className="metric-change">{metrics?.activeUsersWeek || 0} this week</span>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">📚</div>
                <div className="metric-info">
                  <h3>{metrics?.totalBooks || 0}</h3>
                  <p>Total Books</p>
                  <span className="metric-change">+{metrics?.booksAddedToday || 0} today</span>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">🏛️</div>
                <div className="metric-info">
                  <h3>{metrics?.totalBookClubs || 0}</h3>
                  <p>Book Clubs</p>
                  <span className="metric-change">{metrics?.totalBookLists || 0} lists</span>
                </div>
              </div>
            </div>

            <div className="growth-summary">
              <h3>Growth Summary</h3>
              <div className="growth-metrics">
                <div className="growth-item">
                  <span>New Users (Week)</span>
                  <strong>{metrics?.newUsersWeek || 0}</strong>
                </div>
                <div className="growth-item">
                  <span>New Users (Month)</span>
                  <strong>{metrics?.newUsersMonth || 0}</strong>
                </div>
                <div className="growth-item">
                  <span>Books Added (Week)</span>
                  <strong>{metrics?.booksAddedWeek || 0}</strong>
                </div>
                <div className="growth-item">
                  <span>Books Added (Month)</span>
                  <strong>{metrics?.booksAddedMonth || 0}</strong>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-section">
            <div className="section-header">
              <h3>Recent Users ({allUsers.length})</h3>
            </div>
            <div className="users-list">
              {allUsers.map(user => (
                <div key={user.id} className="user-item">
                  <div className="user-avatar">
                    {user.username?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="user-info">
                    <h4>{user.username}</h4>
                    <p>{user.email}</p>
                    <div className="user-stats">
                      {user.booksCount || 0} books • {user.listsCount || 0} lists
                      {user.isAdmin && <span className="admin-badge">ADMIN</span>}
                    </div>
                  </div>
                  <div className="user-meta">
                    <span>{formatTimeAgo(user.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="activity-section">
            <div className="section-header">
              <h3>Recent Activity ({recentActivity.length})</h3>
            </div>
            <div className="activity-list">
              {recentActivity.length > 0 ? (
                recentActivity.map(activity => (
                  <div key={activity.id} className="activity-item">
                    <div className="activity-icon">
                      {getActivityIcon(activity.action)}
                    </div>
                    <div className="activity-info">
                      <h4>{getActivityDescription(activity.action)}</h4>
                      <p>{activity.username} ({activity.userEmail})</p>
                      {activity.details && <span className="activity-details">{activity.details}</span>}
                    </div>
                    <div className="activity-meta">
                      <span>{formatTimeAgo(activity.timestamp)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-data">
                  <p>No recent activity data available</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'growth' && (
          <div className="growth-section">
            <div className="section-header">
              <h3>Growth Analytics</h3>
              <select 
                value={timeframe} 
                onChange={(e) => setTimeframe(e.target.value)}
                className="timeframe-select"
              >
                <option value="day">24 Hours</option>
                <option value="week">7 Days</option>
                <option value="month">30 Days</option>
              </select>
            </div>
            
            <div className="growth-chart">
              {growthData.length > 0 ? (
                <div className="chart-container">
                  <div className="chart-header">
                    <h4>New User Registrations</h4>
                  </div>
                  <div className="simple-chart">
                    {growthData.map((data, index) => (
                      <div key={index} className="chart-bar">
                        <div 
                          className="bar" 
                          style={{
                            height: `${Math.max(10, data.users * 20)}px`,
                            backgroundColor: data.users > 0 ? '#4CAF50' : '#e0e0e0'
                          }}
                        ></div>
                        <span className="bar-label">
                          {data.date.getMonth() + 1}/{data.date.getDate()}
                        </span>
                        <span className="bar-value">{data.users}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="no-data">
                  <p>No growth data available</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="dashboard-actions">
        <button onClick={loadAdminData} className="refresh-button">
          Refresh Data
        </button>
      </div>
    </div>
  );
};

const getActivityIcon = (action) => {
  const icons = {
    user_registered: '👤',
    user_signed_in: '🔑',
    book_added: '📖',
    book_updated: '✏️',
    book_club_created: '🏛️',
    book_club_joined: '➕',
    friend_request_sent: '📬',
    friend_request_accepted: '✅',
    list_created: '📝',
    list_updated: '📋',
    profile_updated: '👤',
    book_rated: '⭐',
    book_finished: '🎉',
    app_launched: '🚀'
  };
  return icons[action] || '📱';
};

const getActivityDescription = (action) => {
  const descriptions = {
    user_registered: 'User Registered',
    user_signed_in: 'User Signed In',
    book_added: 'Book Added',
    book_updated: 'Book Updated',
    book_club_created: 'Book Club Created',
    book_club_joined: 'Joined Book Club',
    friend_request_sent: 'Friend Request Sent',
    friend_request_accepted: 'Friend Request Accepted',
    list_created: 'List Created',
    list_updated: 'List Updated',
    profile_updated: 'Profile Updated',
    book_rated: 'Book Rated',
    book_finished: 'Book Finished',
    app_launched: 'App Launched'
  };
  return descriptions[action] || action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export default AdminDashboard;